import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose, withHandlers } from 'recompose';
import exact from 'prop-types-exact';
import { Button, ButtonToolbar, Clearfix, Glyphicon, Label, Nav, NavItem } from 'react-bootstrap';

import { nodeType } from '../../../propTypes';
import linkWithCredentials from '../../../utils/linkWithCredentials';
import { getFormState } from '../../tickets/components/TicketVariablesEditor';
import { isApolloGraphQLError } from '../../../utils/errorTypeCheck';

import MissingTimeEntriesModal, {
  missingTimeEntriesModalPropTypes,
  withMissingTimeEntriesModalState,
} from './MissingTimeEntriesModal';

import UnreviewReasonModal, {
  unreviewReasonModalPropTypes,
  withUnreviewReasonModalState,
} from '../../ticketInvoicing/containers/UnreviewReasonModal';

import { unreviewJobId } from '../actions';
import { getCurrentJobId } from '../selectors';
import Overlay from '../../../components/Overlay/index';
import { downloadingFileAndUpdatingCredentials } from '../../../utils/downloadUpdatingCredentials';

const propTypes = exact({
  tickets: PropTypes.arrayOf(nodeType).isRequired,
  job: nodeType.isRequired,
  selectedTicketId: PropTypes.number.isRequired,
  selectTicketId: PropTypes.func.isRequired,
  approveJobId: PropTypes.func.isRequired,
  apiServerUrl: PropTypes.string.isRequired,
  pristine: PropTypes.bool.isRequired,
  children: PropTypes.node,
  jobId: PropTypes.number,
  unreviewJobIdAction: PropTypes.func.isRequired,
  unreviewJob: PropTypes.func.isRequired,

  ...missingTimeEntriesModalPropTypes,
  ...unreviewReasonModalPropTypes,
});


const JobApprovalPanel = (props) => {
  const {
    tickets,
    job,
    apiServerUrl,
    selectedTicketId,
    selectTicketId,
    approveJobId,
    pristine,
  } = props;

  const {
    showMissingTimeEntriesModal,
    openMissingTimeEntriesModal,
    closeMissingTimeEntriesModal,
  } = props;

  const {
    showUnreviewReasonModal,
    closeUnreviewReasonModal,
    openUnreviewReasonModal,
  } = props;

  const {
    unreviewReasonError,
    setUnreviewReasonError,
    unreviewJob,
  } = props;

  const renderTab = (ticket) => {
    const { id, ticket_number, state } = ticket;
    const stateMap = {
      reviewed: 'primary',
      unreviewed: 'danger',
    };
    const tabStyle = stateMap[state];
    const handleClick = () => { selectTicketId(id); };
    return (
      <NavItem key={id} eventKey={id} onClick={handleClick}>
        <Label bsStyle={tabStyle}>{ticket_number.number}</Label>
      </NavItem>
    );
  };

  const isJobReviewedOrPristine = job.all_reviewed && pristine;
  const isStateUnreviewed = job.state === 'unreviewed';
  const disableJobApprove = !isJobReviewedOrPristine || isStateUnreviewed;

  const handlePdfDownload = () => {
    const pdfUrl = linkWithCredentials(`${apiServerUrl}${job.pdf_path}`);
    downloadingFileAndUpdatingCredentials(pdfUrl);
  };

  const handleApprovingJobId = async () => {
    const { hasTimeEntries } = job;

    if (hasTimeEntries) {
      approveJobId();
    } else {
      openMissingTimeEntriesModal();
    }
  };

  return (
    <div>
      <Nav bsStyle="tabs" activeKey={selectedTicketId}>
        {tickets.map(renderTab, this)}
      </Nav>
      {/* FIXME: pull panel-tabbed class out to App.css */}
      <div className="panel panel-default panel-tabbed">
        {props.children}
        <div className="panel-footer">
          <Clearfix>
            <ButtonToolbar className="pull-left">
              <Button
                bsStyle="info"
                onClick={handlePdfDownload}
              >
                <Glyphicon glyph="export" style={{ marginRight: '0.5em' }} />
                Export Tickets as PDF
              </Button>
            </ButtonToolbar>
            <ButtonToolbar className="pull-right">

              <div className="inline-btn-styling">
                <Overlay enabled={isStateUnreviewed} message="Job is in Field Admin Queue">
                  <div className="tooltip-area">
                    <Button
                      disabled={isStateUnreviewed}
                      bsStyle="danger"
                      onClick={openUnreviewReasonModal}
                    >
                      <Glyphicon glyph="send" style={{ marginRight: '0.5em' }} />
                      Return to Field Admin
                    </Button>
                  </div>
                </Overlay>
              </div>
              <div className="inline-btn-styling">
                <Overlay enabled={disableJobApprove} message="Field Admin has not marked ready to review.">
                  <div className="tooltip-area">
                    <Button bsStyle="primary" disabled={disableJobApprove} onClick={handleApprovingJobId}>
                      <Glyphicon glyph="envelope" style={{ marginRight: '0.5em' }} />
                      Approve for Invoicing
                    </Button>
                  </div>
                </Overlay>
              </div>
            </ButtonToolbar>
          </Clearfix>
        </div>
      </div>
      <MissingTimeEntriesModal
        title="Missing Time Entries"
        message="Missing Time Entries on Job"
        show={showMissingTimeEntriesModal}
        onHide={closeMissingTimeEntriesModal}
        onSubmit={approveJobId}
      />
      <UnreviewReasonModal
        show={showUnreviewReasonModal}
        onHide={closeUnreviewReasonModal}
        disabled={false}
        onSubmit={unreviewJob}
        error={unreviewReasonError}
        setError={setUnreviewReasonError}
      />
    </div>
  );
};


JobApprovalPanel.propTypes = propTypes;

const mapStateToProps = (state) => {
  const { pristine } = getFormState(state);
  return {
    jobId: getCurrentJobId(state),
    pristine,
  };
};

const mapDispatchToProps = {
  unreviewJobIdAction: unreviewJobId,
};

const withRedux = connect(mapStateToProps, mapDispatchToProps);


export const withUnreviewJob = withHandlers({
  unreviewJob: (props) => {
    return async (reason) => {
      const {
        jobId,
        unreviewJobIdAction,
        setUnreviewReasonDisabled,
        setUnreviewReasonError,
        clearUnreviewReasonError,
      } = props;

      clearUnreviewReasonError();
      setUnreviewReasonDisabled(true);
      try {
        await unreviewJobIdAction(jobId, reason);
      } catch (e) {
        if (isApolloGraphQLError(e)) {
          setUnreviewReasonDisabled(false);
          setUnreviewReasonError(e.message);
          return;
        }
        throw e;
      }
    };
  },
});

const enhance = compose(
  withRedux,
  withMissingTimeEntriesModalState,
  withUnreviewReasonModalState,
  withUnreviewJob,
);

export default enhance(JobApprovalPanel);
